import { useCallback, useEffect, useMemo, useState } from 'react';


interface UseSelectionProps {
	targetIds: string[];
	total: number;
}

type SelectionStatus = 'all' | 'none' | 'partial'

export const useSelection = ({ targetIds, total }: UseSelectionProps) => {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

	const selectionCount = useMemo(() => isAllSelected ? total : selectedIds.length, [isAllSelected, total, selectedIds]);

	const markOne = useCallback((id: string, isMarking: boolean) => {
		if (isMarking) {
			if (selectedIds.includes(id)) {
				return;
			}
			setSelectedIds([...selectedIds, id]);
		}
		else {
			setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
			setIsAllSelected(false);
		}
	}, [selectedIds]);

	const markPage = useCallback((isMarking: boolean) => {
		if (isMarking) {
			setSelectedIds([...targetIds]);
		} else {
			setSelectedIds([]);
			setIsAllSelected(false);
		}
	}, [targetIds]);

	const markAll = useCallback((isMarking: boolean) => {
		if (isMarking) {
			setIsAllSelected(true);
		} else {
			setIsAllSelected(false);
			setSelectedIds([]);
		}
	}, []);

	const pageSelectionStatus = useMemo((): SelectionStatus => {
		if (selectedIds.length === 0) {
			return 'none';
		}
		if (selectedIds.length === targetIds.length) {
			return 'all';
		}
		return 'partial';
	}, [selectedIds, targetIds]);

	useEffect(() => {
		setIsAllSelected(false);
		if (selectedIds.length > 0) {
			setSelectedIds([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [targetIds]);

	useEffect(() => {
		if (isAllSelected) {
			setSelectedIds(targetIds);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAllSelected]);

	const clearSelection = useCallback(() => {
		setSelectedIds([]);
		setIsAllSelected(false);
	}, []);

	return useMemo(() => {
		return {
			isAllSelected,
			pageSelectionStatus,
			selectionCount,
			selectedIds,
			markOne,
			markPage,
			markAll,
			clearSelection,
		};
	}, [selectionCount, pageSelectionStatus, isAllSelected, selectedIds, markOne, markPage, markAll, clearSelection]);
}; 